import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import {
  DatePicker,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  UserNav,
} from '../../components';
import { TopbarContainer } from '..';
import config from '../../config';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import css from './BusinessTestPage.css';

const CZK_TO_PLN = 0.1742;

const toIsoLocal = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Funkcja pomocnicza: odejmuje 364 dni (52 tygodnie), aby trafić w ten sam dzień tygodnia rok temu
const getSameDayWeeksAgo = (dateString, weeksAgo = 52) => {
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - (weeksAgo * 7));
  return toIsoLocal(d);
};

const today = new Date(); // local time

const yesterday = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() - 1
);

const sameDayLastYear = new Date(
  yesterday.getFullYear() - 1,
  yesterday.getMonth(),
  yesterday.getDate() + 2
);

const currentYear = today.getFullYear();
const currentMonth = today.getMonth();

const firstDay = new Date(currentYear, currentMonth, 1);
const lastDay = today;
const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

const sameDayLastYearISO = toIsoLocal(sameDayLastYear);
const yesterdayISO = toIsoLocal(yesterday);
const firstDayISO = toIsoLocal(firstDay);
const lastDayISO = toIsoLocal(lastDay);
const lastDayOfMonthISO = toIsoLocal(lastDayOfMonth);

const countryData = [
  { value: 'Polska (PLN)', countryId: 'pl', currency: 'PLN' },
  { value: 'Magyarország (HUF)', countryId: 'hu', currency: 'HUF' },
  { value: 'Česká republika (CZK)', countryId: 'cz', currency: 'CZK' },
  { value: 'Slovensko (EUR)', countryId: 'sk', currency: 'EUR' }
];

const renderPercentageChange = (current, prev) => {
  if (!prev || prev === 0) return <span className={css.neutral}>--</span>;
  const change = ((current - prev) / prev) * 100;
  const isPositive = change >= 0;
  return (
    <span className={isPositive ? css.positiveChange : css.negativeChange}>
      {isPositive ? '+' : ''}{change.toFixed(1)}%
    </span>
  );
};

// Konwertuje tablicę danych na obiekt { "YYYY-MM-DD": atrybuty } dla błyskawicznego wyszukiwania po dacie
const dataToMap = (apiResponse) => {
  if (!apiResponse || !apiResponse.data) return {};
  return apiResponse.data.slice(1).reduce((acc, item) => {
    const date = item.attributes?.createdAt;
    if (date) {
      acc[date] = item.attributes;
    }
    return acc;
  }, {});
};

const PL_HOURLY_DISTRIBUTION = [
  0.011, 0.018, 0.020, 0.021, 0.022, 0.023, 0.026, 0.042, 0.071, 0.129,
  0.202, 0.278, 0.352, 0.415, 0.479, 0.539, 0.603, 0.663, 0.744, 0.807,
  0.865, 0.925, 0.972, 1.000
];

const CZ_HOURLY_DISTRIBUTION = [
  0.009, 0.013, 0.014, 0.015, 0.016, 0.018, 0.029, 0.053, 0.094, 0.157,
  0.248, 0.322, 0.386, 0.450, 0.510, 0.570, 0.619, 0.677, 0.737, 0.804,
  0.873, 0.937, 0.979, 1.000
];

const getTodayPacingEstimation = (currentValue, country = 'pl') => {
  const now = new Date();
  const currentHour = now.getHours(); // 0 - 23

  const distribution = country === 'cz' ? CZ_HOURLY_DISTRIBUTION : PL_HOURLY_DISTRIBUTION;
  const expectedProgress = distribution[currentHour] || 1.0;

  return currentValue / expectedProgress;
};

export const BusinessForecastPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    location,
    history,
    onLoadData,
    currentUser = null
  } = props;

  const countryUser = countryData.find(({ countryId }) => countryId === currentUser?.attributes?.language);
  const defaultCurrency = countryUser ? countryUser.currency : 'PLN';
  const defaultCountryId = config.custom.countryId;

  const {
    start = toIsoLocal(today),
    end = toIsoLocal(today),
    prev_start = sameDayLastYearISO,
    prev_end = sameDayLastYearISO,
    currency = defaultCurrency,
    countryId = defaultCountryId,
    isDecathlon
  } = parse(location.search);

  const [plData, setPlData] = useState(null);
  const [czData, setCzData] = useState(null);
  const [plPrevData, setPlPrevData] = useState(null);
  const [czPrevData, setCzPrevData] = useState(null);

  useEffect(() => {
    Promise.all([
      onLoadData({ createdAt: '*', createdAtGreaterThan: start, createdAtLessThan: end, currency: 'PLN', isDecathlon: true }),
      onLoadData({ createdAt: '*', createdAtGreaterThan: start, createdAtLessThan: end, currency: 'CZK', isDecathlon: true }),
      prev_start && prev_end ? onLoadData({ createdAt: '*', createdAtGreaterThan: prev_start, createdAtLessThan: prev_end, currency: 'PLN', isDecathlon: true }) : Promise.resolve({ data: null }),
      prev_start && prev_end ? onLoadData({ createdAt: '*', createdAtGreaterThan: prev_start, createdAtLessThan: prev_end, currency: 'CZK', isDecathlon: true }) : Promise.resolve({ data: null })
    ]).then((res) => {
      setPlData(res[0].data);
      setCzData(res[1].data);
      setPlPrevData(res[2].data);
      setCzPrevData(res[3].data);
    })
  }, [start, end, prev_start, prev_end, currency, countryId, isDecathlon])

  const title = intl.formatMessage({ id: "BusinessForecastPage.title" });

  const handleDateRangeSubmit = ({ start, end, prevStart, prevEnd }) => {
    const prevDates = prevStart && prevEnd ? { prev_start: prevStart, prev_end: prevEnd } : {};
    history.push(
      createResourceLocatorString(
        'BusinessTestPage',
        routeConfiguration(),
        {},
        { start, end, currency, countryId, isDecathlon, ...prevDates }
      )
    );
  };

  // Zamiana tablic historycznych na mapy słownikowe (O(1) wyszukiwania)
  const plPrevMap = dataToMap(plPrevData);
  const czPrevMap = dataToMap(czPrevData);

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="BusinessDktPage"
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
          <UserNav selectedPageName="BusinessDktPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="BusinessDktPage" />
        <LayoutWrapperMain className={css.colorBackground}>
          <div className={css.content}>
            <div className={css.header}>
              <h1 className={css.title}>
                {intl.formatMessage({ id: 'BusinessDktPage.yourResults' })}
              </h1>
              <div>
                <DatePicker
                  onSubmit={handleDateRangeSubmit}
                  value={{ startDate: start, endDate: end, prevStartDate: prev_start, prevEndDate: prev_end }}
                  showComparison
                  futureDatesOn
                />
              </div>
            </div>

            <div className={css.tables}>
              {/* POLSKA */}
              {plData && (
                <div className={css.tableSection}>
                  <div className={css.tableHeaderMain}>
                    <h2>Polska - {(plData.data[0]?.attributes.payinTotal / 100).toFixed(2)} PLN</h2>
                    {plPrevData?.data?.[0] && (
                      <div className={css.totalComparison}>
                        <span className={css.smallPrev}>poprzednio: {(plPrevData.data[0].attributes.payinTotal / 100).toFixed(2)} PLN</span>
                        <div className={css.trendLarge}>
                          {renderPercentageChange(plData.data[0].attributes.payinTotal, plPrevData.data[0].attributes.payinTotal)}
                        </div>
                      </div>
                    )}
                  </div>

                  {plData.data.slice(1).reverse().map((day) => {
                    const currentVal = day.attributes.payinTotal / 100;
                    const targetPrevDate = getSameDayWeeksAgo(day.attributes.createdAt);
                    const prevDayAttr = plPrevMap[targetPrevDate];
                    const prevVal = prevDayAttr ? prevDayAttr.payinTotal / 100 : null;

                    // Sprawdzamy, czy ten wiersz w pętli to dzisiejszy dzień
                    const isTodayRow = day.attributes.createdAt === toIsoLocal(new Date());
                    const estimatedTotal = isTodayRow ? getTodayPacingEstimation(currentVal, 'pl') : null;

                    return (
                      <div key={`pl-${day.attributes.createdAt}`} className={css.dayRow}>
                        <div className={css.dayValue}>
                          {day.attributes.createdAt}
                          {isTodayRow && <span className={css.todayBadge} style={{ color: '#0070f3', fontWeight: 'bold' }}> (DZISIAJ)</span>}
                          {prevDayAttr && <span className={css.dateCompareHint}> vs {targetPrevDate}</span>}
                        </div>
                        <div className={css.valuesContainer}>
                          <div className={css.amount}>
                            {currentVal.toFixed(2)} PLN

                            {/* Wyświetlanie estymacji TYLKO dla dzisiejszego dnia w PLN */}
                            {isTodayRow && estimatedTotal && (
                              <div className={css.estimationBox} style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                Prognoza końca dnia: <strong>{estimatedTotal.toFixed(2)} PLN</strong>
                                <span style={{ fontSize: '0.8em', display: 'block', color: '#999' }}>
                                  (na bazie {(PL_HOURLY_DISTRIBUTION[new Date().getHours()] * 100).toFixed(1)}% realizacji o godz. {new Date().getHours()}:00)
                                </span>
                              </div>
                            )}
                          </div>

                          {prevVal !== null && (
                            <div className={css.comparisonBox}>
                              <span className={css.smallPrev}>
                                {isTodayRow ? `rok temu: ${prevVal.toFixed(2)} PLN` : `${prevVal.toFixed(2)} PLN`}
                              </span>
                              <span className={css.trendBadge}>
                                {/* Porównanie trendu dla PL: prognoza vs zamknięty dzień sprzed roku */}
                                {isTodayRow
                                  ? renderPercentageChange(estimatedTotal, prevVal)
                                  : renderPercentageChange(currentVal, prevVal)
                                }
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* CZECHY */}
              {czData && (
                <div className={css.tableSection}>
                  <div className={css.tableHeaderMain}>
                    <h2>Czechy - {(czData.data[0]?.attributes.payinTotal * CZK_TO_PLN / 100).toFixed(2)} PLN</h2>
                    {czPrevData?.data?.[0] && (
                      <div className={css.totalComparison}>
                        <span className={css.smallPrev}>poprzednio: {(czPrevData.data[0].attributes.payinTotal * CZK_TO_PLN / 100).toFixed(2)} PLN</span>
                        <div className={css.trendLarge}>
                          {renderPercentageChange(czData.data[0].attributes.payinTotal, czPrevData.data[0].attributes.payinTotal)}
                        </div>
                      </div>
                    )}
                  </div>

                  {czData.data.slice(1).reverse().map((day) => {
                    // Wartość bieżąca przeliczona na PLN
                    const currentVal = (day.attributes.payinTotal * CZK_TO_PLN) / 100;
                    const targetPrevDate = getSameDayWeeksAgo(day.attributes.createdAt);
                    const prevDayAttr = czPrevMap[targetPrevDate];
                    const prevVal = prevDayAttr ? (prevDayAttr.payinTotal * CZK_TO_PLN) / 100 : null;

                    // Sprawdzamy, czy ten wiersz w pętli to dzisiejszy dzień
                    const isTodayRow = day.attributes.createdAt === toIsoLocal(new Date());
                    const estimatedTotal = isTodayRow ? getTodayPacingEstimation(currentVal, 'cz') : null;

                    return (
                      <div key={`cz-${day.attributes.createdAt}`} className={css.dayRow}>
                        <div className={css.dayValue}>
                          {day.attributes.createdAt}
                          {isTodayRow && <span className={css.todayBadge} style={{ color: '#0070f3', fontWeight: 'bold' }}> (DZISIAJ)</span>}
                          {prevDayAttr && <span className={css.dateCompareHint}> vs {targetPrevDate}</span>}
                        </div>
                        <div className={css.valuesContainer}>
                          <div className={css.amount}>
                            {currentVal.toFixed(2)}

                            {/* Wyświetlanie estymacji TYLKO dla dzisiejszego dnia */}
                            {isTodayRow && estimatedTotal && (
                              <div className={css.estimationBox} style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                Prognoza końca dnia: <strong>{estimatedTotal.toFixed(2)} PLN</strong>
                                <span style={{ fontSize: '0.8em', display: 'block', color: '#999' }}>
                                  (na bazie {(CZ_HOURLY_DISTRIBUTION[new Date().getHours()] * 100).toFixed(1)}% realizacji o godz. {new Date().getHours()}:00)
                                </span>
                              </div>
                            )}
                          </div>

                          {prevVal !== null && (
                            <div className={css.comparisonBox}>
                              <span className={css.smallPrev}>
                                {isTodayRow ? `rok temu: ${prevVal.toFixed(2)}` : prevVal.toFixed(2)}
                              </span>
                              <span className={css.trendBadge}>
                                {/* Dla dnia dzisiejszego porównujemy PROGNOZĘ Czech do ich wyniku sprzed roku */}
                                {isTodayRow
                                  ? renderPercentageChange(estimatedTotal, prevVal)
                                  : renderPercentageChange(currentVal, prevVal)
                                }
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const loadData = params => (dispatch, getState, sdk) => {
  return sdk.newSdk.analyticsBookings.query({ ...params, })
    .then((res) => {
      return res;
    })
    .catch(e => {
      console.error(e, 'initial-message-send-failed', { tx: 'PLN' });
    });
};

const mapDispatchToProps = dispatch => ({
  onLoadData: (params) => dispatch(loadData(params))
});

const BusinessForecastPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withRouter
)(BusinessForecastPageComponent);

export default BusinessForecastPage;